import { MarketType } from '@velocity-exchange/sdk';
import { MultiplexWebSocket } from '@velocity-exchange/common';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PricingDataFromWebSocket,
	PricingWebSocketMessage,
} from 'src/hooks/query/pricing/types';

export interface ProcessedPricingData {
	symbol: string;
	marketIndex: number;
	marketType: MarketType;
	oraclePrice: number;
	markPrice: number;
	lastPrice: number;
	timestamp: number;
}

export interface PricingWebsocketClientConfig {
	websocketUrl: string;
	onConnectionStateChange: (connected: boolean) => void;
	onError: (error: Error) => void;
}

export class PricingWebsocketClient {
	private config: PricingWebsocketClientConfig;
	private pricingSubject = new Subject<ProcessedPricingData[]>();
	private destroy$ = new Subject<void>();
	private multiplexSubscription: { unsubscribe: () => void } | null = null;
	private hasConnected = false;

	constructor(config: PricingWebsocketClientConfig) {
		this.config = config;
	}

	subscribe(): void {
		// Cleanup existing subscription to prevent memory leaks
		if (this.multiplexSubscription) {
			this.multiplexSubscription.unsubscribe();
			this.multiplexSubscription = null;
		}

		this.multiplexSubscription =
			MultiplexWebSocket.createWebSocketSubscription<PricingWebSocketMessage>({
				wsUrl: this.config.websocketUrl,
				subscriptionId: 'pricing',
				subscribeMessage: JSON.stringify({
					type: 'subscribe',
					channelType: 'pricing',
				}),
				unsubscribeMessage: JSON.stringify({
					type: 'unsubscribe',
					channelType: 'pricing',
				}),
				onMessage: (message) => {
					if (!this.hasConnected) {
						this.hasConnected = true;
						this.config.onConnectionStateChange(true);
					}

					if (
						(message.type === 'update' || message.type === 'init') &&
						message.data
					) {
						const processed = this.processData(message.data);
						if (processed.length > 0) {
							this.pricingSubject.next(processed);
						}
					}
				},
				messageFilter: (message) => {
					return (
						message.channelType === 'pricing' &&
						(message.type === 'update' ||
							message.type === 'init' ||
							message.type === 'subscription')
					);
				},
				onError: (error) => {
					this.config.onConnectionStateChange(false);
					this.config.onError(
						error instanceof Error ? error : new Error(String(error))
					);
				},
				onClose: () => {
					if (this.hasConnected) {
						this.hasConnected = false;
						this.config.onConnectionStateChange(false);
					}
				},
				errorMessageFilter: (message?: any) => {
					return !!message?.error;
				},
			});
	}

	getPricingDataStream(): Observable<ProcessedPricingData[]> {
		return this.pricingSubject.asObservable().pipe(takeUntil(this.destroy$));
	}

	destroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.multiplexSubscription) {
			this.multiplexSubscription.unsubscribe();
			this.multiplexSubscription = null;
		}
		this.pricingSubject.complete();
	}

	private processData(
		data: PricingDataFromWebSocket[]
	): ProcessedPricingData[] {
		return data.map((item) => {
			const oraclePrice = parseFloat(item.oraclePrice) || 0;
			const markPrice = parseFloat(item.markPrice ?? '0') || 0;
			const lastPrice = parseFloat(item.price ?? '0') || markPrice;

			return {
				symbol: item.symbol,
				marketIndex: item.marketIndex,
				marketType:
					item.marketType === 'perp' ? MarketType.PERP : MarketType.SPOT,
				oraclePrice,
				markPrice,
				lastPrice,
				timestamp: Date.now(),
			};
		});
	}
}
